import { useEffect, useRef } from 'react';
import { getUpdatedDesignWithSignature } from '@/components/UnlayerEditorWrapper/unlayerSignatureDesign';

/**
 * Applies signature requests to the current Unlayer design.
 * The parent only provides an id/html request; this hook handles reading the
 * live design, rebuilding it with the signature block, and reloading it safely.
 */
export const useUnlayerSignatureInsertion = ({
	editorReadyRevision,
	editorRef,
	signatureInsertRequest,
	flushDesignChangeAfterProgrammaticLoad,
}) => {
	const lastAppliedSignatureRequestRef = useRef(null);
	const signatureRequestId = signatureInsertRequest?.id ?? null;

	// Rebuild the current design with the newly selected signature and push the
	// updated html/design snapshot back to Formik.
	useEffect(() => {
		const signatureHtml = signatureInsertRequest?.html;
		const waitForDesignLoad = signatureInsertRequest?.waitForDesignLoad === true;
		if (!signatureRequestId || typeof signatureHtml !== 'string') {
			return;
		}

		// Signature insertion works against the current design snapshot, so we need
		// access to both saveDesign and loadDesign on the live editor instance.
		const unlayer = editorRef.current?.editor;
		if (
			!unlayer ||
			typeof unlayer.saveDesign !== 'function' ||
			typeof unlayer.loadDesign !== 'function'
		) {
			return;
		}

		if (lastAppliedSignatureRequestRef.current === signatureRequestId) {
			return;
		}

		let didApply = false;
		let applyTimeoutId = null;
		let cleanupSignatureDesignLoaded = null;
		const applySignatureToCurrentDesign = () => {
			if (didApply) {
				return;
			}
			didApply = true;
			lastAppliedSignatureRequestRef.current = signatureRequestId;

			unlayer.saveDesign((design) => {
				// The helper removes older signature blocks and appends the requested
				// signature as a dedicated bottom row so placement is stable regardless
				// of how the rest of the design is structured.
				const nextDesign = getUpdatedDesignWithSignature(design, signatureHtml);
				if (nextDesign && nextDesign !== design) {
					if (
						typeof unlayer.addEventListener === 'function' &&
						typeof unlayer.removeEventListener === 'function'
					) {
						const handleSignatureDesignLoaded = () => {
							unlayer.removeEventListener('design:loaded', handleSignatureDesignLoaded);
							flushDesignChangeAfterProgrammaticLoad();
						};
						unlayer.addEventListener('design:loaded', handleSignatureDesignLoaded);
						cleanupSignatureDesignLoaded = () => {
							unlayer.removeEventListener('design:loaded', handleSignatureDesignLoaded);
						};
					}
					unlayer.loadDesign(nextDesign);
					return;
				}
				flushDesignChangeAfterProgrammaticLoad();
			});
		};

		// Initial signature auto-selection can happen before the editor finishes
		// loading its first design. Listen for that load completion, but also keep
		// a short timeout fallback for normal manual selections where no load event
		// is expected before we mutate the current design.
		const handleDesignLoaded = () => {
			if (typeof unlayer.removeEventListener === 'function') {
				unlayer.removeEventListener('design:loaded', handleDesignLoaded);
			}
			if (applyTimeoutId) {
				clearTimeout(applyTimeoutId);
			}
			applySignatureToCurrentDesign();
		};

		if (typeof unlayer.addEventListener === 'function') {
			unlayer.addEventListener('design:loaded', handleDesignLoaded);
		}

		if (!waitForDesignLoad) {
			applyTimeoutId = setTimeout(() => {
				if (typeof unlayer.removeEventListener === 'function') {
					unlayer.removeEventListener('design:loaded', handleDesignLoaded);
				}
				applySignatureToCurrentDesign();
			}, 200);
		}

		return () => {
			if (applyTimeoutId) {
				clearTimeout(applyTimeoutId);
			}
			if (cleanupSignatureDesignLoaded) {
				cleanupSignatureDesignLoaded();
			}
			if (typeof unlayer.removeEventListener === 'function') {
				unlayer.removeEventListener('design:loaded', handleDesignLoaded);
			}
		};
	}, [
		editorReadyRevision,
		editorRef,
		flushDesignChangeAfterProgrammaticLoad,
		signatureInsertRequest?.html,
		signatureInsertRequest?.waitForDesignLoad,
		signatureRequestId,
	]);
};
